import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { DynamicUserFields } from './DynamicUserFields';
import { IdentifierFields } from './identifierFields';
import { RoleSelect } from './RoleSelect';
import { useUserCreateFormCore, type UserCreateFormValues } from './useUserCreateFormCore';
import { useWatch } from 'react-hook-form';
import { handleApiError, resolveErrorMessage } from '../../api/errorMapping';
import { ApiError } from '../../api/errors';
import { tenantApi } from '../../api/endpoints/tenant';
import { allowedScopesForRole } from '../../lib/roleScopes';
import {
    API_ROLE_DISABLED_NOTE,
    API_USER_MANAGE_PERMISSION,
    isRoleForbiddenForApiUser,
} from '../../lib/apiUserRoles';
import { parseIpAllowlist } from '../../lib/apiUserIpAllowlist';
import { generateIdempotencyKey } from '../../lib/idempotency';
import { useAuth } from '../../hooks/useAuth';
import { Button, Input as KitInput } from '@fiado/react-kit';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { AccountTypeSelector } from './AccountTypeSelector';
import { IpAllowlistField } from '../shared/IpAllowlistField';
import { ApiUserCredentialsModal } from '../shared/ApiUserCredentialsModal';
import type { AccountTypeLocal, ApiUserCredentialsLocal } from '../../api/types/TenantLocal';
import type { ScopeDescriptor } from './scopeDescriptors';
import { useDestinationStrategy, type DestinationSubmitResult } from './useDestinationStrategy';

interface Props {
    /** Scope del flujo (TENANT). Decide el comportamiento de destino + creación. */
    descriptor: ScopeDescriptor;
    /**
     * Ref del contexto: el tenantId. Si se omite, se usa `descriptor.useScopeRef()` (el tenantId del actor).
     */
    scopeRef?: string;
    /** `onDone` se llama tras crear OK (ej. cerrar el modal). Si no se pasa, navega a la lista de usuarios. */
    onDone?: () => void;
}

/** Códigos del backend que se corrigen en el campo de IPs. */
const IP_ALLOWLIST_ERROR_CODES: ReadonlySet<string> = new Set([
    'IP_ALLOWLIST_REQUIRED',
    'INVALID_IP_ALLOWLIST',
    'IP_ALLOWLIST_TOO_BROAD',
    'IP_ALLOWLIST_TOO_LONG',
    'IP_ALLOWLIST_NOT_PUBLIC',
]);

/**
 * Form de alta de usuario del flujo TENANT. Lo común (schema, useForm, query+filtrado de roles, el
 * <select> de rol seccionado) vive aquí; el bloque de destino + el submit se delegan a
 * `useDestinationStrategy(descriptor, scopeRef, roleScope)`.
 *
 * Los roles SIEMPRE son los del tenant (`tenantApi.listRoles(tid)`).
 */
export function ScopedUserForm({ descriptor, scopeRef: scopeRefProp, onDone }: Props) {
    const descriptorScopeRef = descriptor.useScopeRef();
    // scopeRef del contexto: prop (drill-down platform) o el tenant del actor (flujo propio).
    const scopeRef = scopeRefProp ?? descriptorScopeRef ?? '';
    const isTenant = descriptor.scope === 'TENANT';

    // DEC-RBAC-079/081: identificador PRIMARIO del tenant del `scopeRef` (propio o del path según el scope).
    const { principalIdentifier } = descriptor.useIdentifier(scopeRef);
    // Destino TENANT y resolvedor salen del tenant del scopeRef (propio o el visto en platform).
    const tenantDestLabel = descriptor.useTenantDestLabel(scopeRef);
    const labels = descriptor.useLabels(scopeRef);

    // El selector Persona / Sistema (API) solo aparece si el actor administra usuarios de API.
    // El backend re-valida el permiso con el scope del tenant.
    const { hasPermission, hasScopeAccess } = useAuth();
    const canManageApiUsers =
        hasPermission(API_USER_MANAGE_PERMISSION) && hasScopeAccess(descriptor.scope, scopeRef);
    const [accountType, setAccountType] = useState<AccountTypeLocal>('HUMAN');
    const isApiUser = canManageApiUsers && accountType === 'API';
    // Una key por apertura del form: un reintento tras un timeout no crea dos sistemas.
    const [apiIdempotencyKey] = useState(generateIdempotencyKey);
    // Credenciales del alta: solo en este estado local, nunca en la caché de queries ni de mutaciones.
    const [credentials, setCredentials] = useState<ApiUserCredentialsLocal | null>(null);
    const [createdSystemName, setCreatedSystemName] = useState('');

    // El catálogo de roles + customFields y el destino usan el MISMO tenant del scopeRef.
    const core = useUserCreateFormCore({
        catalogTenantId: scopeRef,
        principalIdentifier,
        rolesQueryKey: descriptor.rolesQueryKey(scopeRef),
        listRoles: () => descriptor.listRoles(scopeRef),
        lateralPermission: `${descriptor.permPrefix}.user.create.lateral`,
        labels,
        accountType: isApiUser ? 'API' : 'HUMAN',
    });
    const { form, roleGroups, roleId, roleScopeRaw, customFields } = core;
    const ipAllowlist = useWatch({ control: form.control, name: 'ipAllowlist' });

    const apiForbiddenRoleIds = useMemo(
        () =>
            new Set(
                roleGroups
                    .flatMap((g) => g.roles)
                    .filter(isRoleForbiddenForApiUser)
                    .map((r) => r.roleId),
            ),
        [roleGroups],
    );

    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // Nivel del destino = scope REAL del rol; fallback a la matriz seed si el rol no trae scope.
    const roleScope = useMemo(() => {
        if (!roleId) return null;
        return roleScopeRaw ?? allowedScopesForRole(roleId)[0];
    }, [roleId, roleScopeRaw]);

    const strategy = useDestinationStrategy({
        scopeRef,
        tid: scopeRef,
        roleScope,
        hasRole: !!roleId,
        createUser: descriptor.api.createUser,
        tenantDestLabel,
        labels,
    });

    const handleRoleChange = (id: string) => {
        core.setRole(id);
        strategy.resetSelection();
    };

    const handleAccountTypeChange = (next: AccountTypeLocal) => {
        setAccountType(next);
        form.clearErrors();
        if (next === 'API' && apiForbiddenRoleIds.has(roleId)) {
            form.setValue('roleId', '');
            strategy.resetSelection();
        }
    };

    // Sin onDone, solo el flujo con página propia navega a su lista (el drill-down vive en un modal).
    const finish = async () => {
        if (onDone) onDone();
        else if (descriptor.defaultDoneRoute) await navigate({ to: descriptor.defaultDoneRoute });
    };

    const invalidateUsers = () =>
        queryClient.invalidateQueries({
            queryKey: [...descriptor.queryKeyBase(scopeRef), 'users'],
        });

    // El secreto se guarda en estado local dentro de mutationFn: lo que regresa no lleva credenciales.
    const apiMut = useMutation({
        mutationFn: async (v: UserCreateFormValues): Promise<{ failedAssignment?: unknown }> => {
            if (!roleScope) throw new Error('NO_ROLE_SCOPE');
            const technicalContact = v.technicalContact.trim();
            const created = await descriptor.createApiUser(
                scopeRef,
                {
                    accountType: 'API',
                    displayName: v.displayName.trim(),
                    ...(technicalContact ? { technicalContact } : {}),
                    ipAllowlist: parseIpAllowlist(v.ipAllowlist),
                },
                { idempotencyKey: apiIdempotencyKey },
            );
            setCreatedSystemName(v.displayName.trim());
            setCredentials({ clientId: created.clientId, clientSecret: created.clientSecret });
            const results = await Promise.allSettled(
                strategy.resolveScopeRefs(roleScope).map((ref) =>
                    tenantApi.assignRole(
                        scopeRef,
                        {
                            cognitoSub: created.cognitoSub,
                            roleId: v.roleId,
                            scope: roleScope,
                            scopeRef: ref,
                        },
                        { idempotencyKey: generateIdempotencyKey() },
                    ),
                ),
            );
            const failed = results.find((r) => r.status === 'rejected');
            return { failedAssignment: failed?.reason };
        },
        onSuccess: async ({ failedAssignment }) => {
            if (failedAssignment) {
                toast.error(
                    `El sistema se creó, pero no se le asignó el rol: ${resolveErrorMessage(failedAssignment)} Asígnaselo desde "Gestionar roles".`,
                );
            } else {
                toast.success('Sistema creado');
            }
            await invalidateUsers();
        },
        onError: async (err) => {
            handleApiError(err);
            if (!(err instanceof ApiError)) return;
            if (IP_ALLOWLIST_ERROR_CODES.has(err.code)) {
                form.setError('ipAllowlist', { message: resolveErrorMessage(err) });
            }
            if (err.code === 'API_USER_ALREADY_CREATED') await invalidateUsers();
        },
    });

    const mut = useMutation({
        mutationFn: (v: UserCreateFormValues): Promise<DestinationSubmitResult> => {
            if (!roleScope) throw new Error('NO_ROLE_SCOPE');
            return strategy.submit(v, roleScope, principalIdentifier);
        },
        onSuccess: async ({ totalRefs, failedExtra, multiSuccess, multiLabel, failLabel }) => {
            if (failedExtra.length > 0) {
                // El user se creó y al menos el primer destino quedó; reportar los extra que fallaron.
                toast.error(
                    `Usuario creado, pero ${String(failedExtra.length)} de ${String(totalRefs)} ${failLabel} fallaron`,
                );
                handleApiError(failedExtra[0].reason);
            } else if (multiSuccess) {
                toast.success(`Usuario creado con el rol en ${String(totalRefs)} ${multiLabel}`);
            } else {
                toast.success('Usuario creado');
            }
            await invalidateUsers();
            if (failedExtra.length === 0) await finish();
        },
        onError: (err) => {
            const strat = handleApiError(err);
            if (strat === 'INLINE' && err instanceof ApiError) {
                if (err.code === 'USER_ALREADY_EXISTS') {
                    // El error inline va sobre el campo PRIMARIO del tenant (email o teléfono).
                    if (principalIdentifier === 'PHONE') {
                        form.setError('phone', { message: 'El teléfono ya existe' });
                    } else {
                        form.setError('email', { message: 'Email ya existe' });
                    }
                }
                // SCOPE_INVALID_FOR_ROLE solo aplicaba al flujo TENANT (UserForm). RetailerUserForm no
                // lo mapeaba; se preserva ese matiz.
                if (isTenant && err.code === 'SCOPE_INVALID_FOR_ROLE') {
                    form.setError('roleId', { message: 'Scope inválido para este role' });
                }
            }
        },
    });

    const idPrefix = isTenant ? 'uf' : 'ruf';
    // RetailerUserForm solo mostraba el error de roleId tras submit; UserForm siempre. Se preserva.
    const showRoleError = isTenant || form.formState.isSubmitted;

    const nameError = isApiUser ? form.formState.errors.displayName?.message : undefined;

    return (
        <>
            <form
                onSubmit={(e) => {
                    void form.handleSubmit((v) => {
                        if (isApiUser) apiMut.mutate(v);
                        else mut.mutate(v);
                    })(e);
                }}
                className="space-y-4"
            >
                {canManageApiUsers && (
                    <AccountTypeSelector
                        value={accountType}
                        onChange={handleAccountTypeChange}
                        idPrefix={idPrefix}
                    />
                )}

                {/* DEC-RBAC-079/081: SOLO el identificador primario del tenant (email o teléfono). */}
                {!isApiUser && (
                    <IdentifierFields
                        form={form}
                        principalIdentifier={principalIdentifier}
                        idPrefix={idPrefix}
                    />
                )}

                <div className="space-y-2">
                    <Label htmlFor={`${idPrefix}-name`}>
                        {isApiUser ? 'Nombre del sistema' : 'Nombre'}
                    </Label>
                    <Input
                        id={`${idPrefix}-name`}
                        placeholder={isApiUser ? 'La Central' : undefined}
                        {...form.register('displayName')}
                    />
                    {nameError && <p className="text-sm text-destructive">{nameError}</p>}
                </div>

                {isApiUser ? (
                    <>
                        <KitInput
                            id={`${idPrefix}-technical-contact`}
                            label="Contacto técnico (opcional)"
                            placeholder="soporte@socio.com"
                            hint="A quién buscar si algo falla con la integración. No recibe correos automáticos."
                            error={form.formState.errors.technicalContact?.message}
                            {...form.register('technicalContact')}
                        />
                        <IpAllowlistField
                            id={`${idPrefix}-ip-allowlist`}
                            value={ipAllowlist}
                            registration={form.register('ipAllowlist')}
                            error={form.formState.errors.ipAllowlist?.message}
                        />
                    </>
                ) : (
                    // Campos personalizados del tenant (entre Nombre y Rol). No renderiza nada sin catálogo.
                    <DynamicUserFields
                        fields={customFields}
                        form={form}
                        idPrefix={`${idPrefix}-cf`}
                    />
                )}

                <RoleSelect
                    groups={roleGroups}
                    value={roleId}
                    onChange={handleRoleChange}
                    idPrefix={idPrefix}
                    error={showRoleError ? form.formState.errors.roleId?.message : undefined}
                    disabledRoleIds={isApiUser ? apiForbiddenRoleIds : undefined}
                    disabledNote={API_ROLE_DISABLED_NOTE}
                />

                {/* Destino según el scope REAL del rol — delegado a la estrategia del scope. */}
                {strategy.DestinationSelector}

                <Button
                    type="submit"
                    isLoading={mut.isPending || apiMut.isPending}
                    disabled={!strategy.canSubmit}
                >
                    Crear
                </Button>
            </form>

            <ApiUserCredentialsModal
                credentials={credentials}
                tenantId={scopeRef}
                systemName={createdSystemName}
                onConfirm={() => {
                    setCredentials(null);
                    void finish();
                }}
            />
        </>
    );
}
